import React, { useState, useRef, useEffect } from 'react';
import './InterviewView.css';

const InterviewView = ({ interviewData, token, apiBaseUrl }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);
  const [recordingTime, setRecordingTime] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('info');

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  // Streaming upload state
  const uploadSessionRef = useRef(null);
  const uploadedPartsRef = useRef([]);
  const currentPartNumberRef = useRef(0);
  const isUploadingRef = useRef(false);
  const chunkBufferRef = useRef([]);  // Buffer for accumulating chunks
  const bufferSizeRef = useRef(0);     // Track buffer size in bytes
  const totalRecordedSizeRef = useRef(0);  // Track total recorded size
  const uploadQueueRef = useRef([]);   // Queue of blobs waiting to be uploaded
  const isProcessingQueueRef = useRef(false);  // Flag to prevent concurrent queue processing
  const isRecordingStoppedRef = useRef(false);  // Flag to track if recording has stopped
  const PART_SIZE_THRESHOLD = 5 * 1024 * 1024;  // 5MB minimum required by R2/S3

  // Utility function to format bytes to MB
  const formatBytes = (bytes) => {
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Get total uploaded size
  const getTotalUploadedSize = () => {
    return uploadedPartsRef.current.reduce((sum, part) => sum + (part.Size || 0), 0);
  };

  const currentQuestion = interviewData.questions[currentQuestionIndex];
  const totalQuestions = interviewData.questions.length;
  const hasRecording = recordedChunks.length > 0;

  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      // Abort any active upload session to clean up R2 multipart uploads
      if (uploadSessionRef.current) {
        fetch(`${apiBaseUrl}/upload/abort/${uploadSessionRef.current.session_id}`, {
          method: 'POST'
        }).catch(err => {
          console.error('Failed to abort upload on unmount:', err);
        });
      }
    };
  }, [apiBaseUrl]);

  const showStatus = (message, type = 'info') => {
    setStatusMessage(message);
    setStatusType(type);
  };

  const setupCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      showStatus('Camera ready', 'success');
    } catch (error) {
      showStatus('Failed to access camera: ' + error.message, 'error');
    }
  };

  const uploadChunk = async (chunk) => {
    if (!uploadSessionRef.current) {
      return;
    }

    // Add chunk to buffer
    chunkBufferRef.current.push(chunk);
    bufferSizeRef.current += chunk.size;
    totalRecordedSizeRef.current += chunk.size;

    const uploadedSize = getTotalUploadedSize();
    const queueLength = uploadQueueRef.current.length;

    console.log(`📦 Buffered: ${formatBytes(bufferSizeRef.current)} (total: ${formatBytes(totalRecordedSizeRef.current)}, queue: ${queueLength}, uploaded: ${formatBytes(uploadedSize)})`);

    // Enqueue buffer when it reaches 5MB threshold
    if (bufferSizeRef.current >= PART_SIZE_THRESHOLD) {
      await uploadBufferedChunks();
    }
  };

  // Process upload queue - upload one blob at a time
  const processUploadQueue = async () => {
    if (isProcessingQueueRef.current || uploadQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;

    while (uploadQueueRef.current.length > 0) {
      const blob = uploadQueueRef.current.shift();  // Take first blob from queue

      try {
        isUploadingRef.current = true;
        currentPartNumberRef.current += 1;
        const partNumber = currentPartNumberRef.current;

        console.log(`⬆️  Uploading part ${partNumber}: ${formatBytes(blob.size)} (queue: ${uploadQueueRef.current.length})`);

        // Get presigned URL for this part
        const urlResponse = await fetch(`${apiBaseUrl}/upload/part-url`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: uploadSessionRef.current.session_id,
            part_number: partNumber
          })
        });

        if (!urlResponse.ok) {
          throw new Error('Failed to get upload URL');
        }

        const { upload_url } = await urlResponse.json();

        // Upload blob to S3/R2
        const uploadResponse = await fetch(upload_url, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': 'video/webm' }
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload chunk');
        }

        // Try both ETag and Etag (case-sensitive)
        let etag = uploadResponse.headers.get('ETag') || uploadResponse.headers.get('Etag') || uploadResponse.headers.get('etag');

        // For Cloudflare R2: ETags may not be exposed via CORS
        if (!etag) {
          etag = 'PLACEHOLDER';
        }

        uploadedPartsRef.current.push({
          PartNumber: partNumber,
          ETag: etag.replace(/"/g, ''),
          Size: blob.size
        });

        const totalUploaded = getTotalUploadedSize();
        console.log(`✅ Part ${partNumber} uploaded (${formatBytes(blob.size)}) | Total uploaded: ${formatBytes(totalUploaded)}`);

        // Update progress
        const totalSize = totalRecordedSizeRef.current;
        if (totalSize > 0) {
          const progress = Math.min(90, (totalUploaded / totalSize) * 90);
          setUploadProgress(progress);
        }

      } catch (error) {
        console.error(`Upload error for part:`, error);
        // Re-add to queue on error
        uploadQueueRef.current.unshift(blob);
        break;  // Stop processing on error
      } finally {
        isUploadingRef.current = false;
      }
    }

    // Reset flag only after loop completes and queue is truly empty
    if (uploadQueueRef.current.length === 0) {
      isProcessingQueueRef.current = false;
    }
  };

  const uploadBufferedChunks = async (uploadAll = false) => {
    if (chunkBufferRef.current.length === 0) {
      return;
    }

    // Merge all buffered chunks into one blob
    const mergedBlob = new Blob(chunkBufferRef.current, { type: 'video/webm' });
    const totalSize = mergedBlob.size;

    if (uploadAll) {
      // Submit phase: split into exact 5MB parts (R2 requires all non-trailing parts same size)
      let offset = 0;
      while (offset < totalSize) {
        const remainingSize = totalSize - offset;
        const partSize = Math.min(PART_SIZE_THRESHOLD, remainingSize);

        const part = mergedBlob.slice(offset, offset + partSize);
        console.log(`📦 Enqueuing part: ${formatBytes(part.size)}`);
        uploadQueueRef.current.push(part);

        offset += partSize;
      }

      // Clear buffer
      chunkBufferRef.current = [];
      bufferSizeRef.current = 0;
    } else {
      // Recording phase: only upload exact 5MB, keep remainder in buffer
      if (totalSize >= PART_SIZE_THRESHOLD) {
        const part = mergedBlob.slice(0, PART_SIZE_THRESHOLD);
        const remainder = mergedBlob.slice(PART_SIZE_THRESHOLD);

        console.log(`📦 Enqueuing exact 5MB part, keeping ${formatBytes(remainder.size)} in buffer`);
        uploadQueueRef.current.push(part);

        // Convert remainder back to chunks (store as single blob)
        chunkBufferRef.current = [remainder];
        bufferSizeRef.current = remainder.size;
      }
    }

    // Start processing the queue
    processUploadQueue();
  };

  const startRecording = async () => {
    if (!streamRef.current) {
      showStatus('Please setup camera first', 'error');
      return;
    }

    try {
      // Initiate upload session
      showStatus('Initializing upload...', 'info');
      const initiateResponse = await fetch(`${apiBaseUrl}/upload/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidate_id: interviewData.candidate_id,
          question_id: currentQuestion.id,
          file_name: 'video.webm',
          content_type: 'video/webm'
        })
      });

      if (!initiateResponse.ok) {
        throw new Error('Failed to initiate upload');
      }

      uploadSessionRef.current = await initiateResponse.json();
      uploadedPartsRef.current = [];
      currentPartNumberRef.current = 0;
      chunkBufferRef.current = [];
      bufferSizeRef.current = 0;
      totalRecordedSizeRef.current = 0;
      uploadQueueRef.current = [];
      isProcessingQueueRef.current = false;
      isRecordingStoppedRef.current = false;  // Reset flag when starting new recording

      console.log('🎥 Recording started');

      // Try different MIME types in order of preference
      let options = { mimeType: 'video/webm' };

      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')) {
        options = { mimeType: 'video/webm;codecs=vp8,opus' };
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')) {
        options = { mimeType: 'video/webm;codecs=vp9,opus' };
      } else if (MediaRecorder.isTypeSupported('video/webm')) {
        options = { mimeType: 'video/webm' };
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        options = { mimeType: 'video/mp4' };
      } else {
        options = {};
      }

      const mediaRecorder = new MediaRecorder(streamRef.current, options);

      const chunks = [];

      // Stream upload each chunk as it becomes available
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
          // Only buffer chunks if recording is active
          // For late chunks (after stop), add to buffer instead of ignoring to prevent data loss
          if (!isRecordingStoppedRef.current) {
            uploadChunk(event.data);
          } else {
            // Late chunks should be added to current buffer to avoid creating tiny parts
            console.log(`📦 Late chunk added to buffer: ${formatBytes(event.data.size)} (recording already stopped)`);
            chunkBufferRef.current.push(event.data);
            bufferSizeRef.current += event.data.size;
          }
        }
      };

      mediaRecorder.onstop = async () => {
        setRecordedChunks(chunks);
        const blob = new Blob(chunks, { type: 'video/webm' });
        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.src = URL.createObjectURL(blob);
        }

        // Just create preview, don't upload final buffer yet
        // Final buffer upload will happen in submitAnswer()
        console.log(`⏹️  Recording stopped | Buffered: ${formatBytes(bufferSizeRef.current)}, Queue: ${uploadQueueRef.current.length}, Uploaded: ${formatBytes(getTotalUploadedSize())}`);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000); // Generate chunk every 1 second
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

      showStatus('Recording and uploading...', 'info');
    } catch (error) {
      showStatus('Failed to start recording: ' + error.message, 'error');
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      // Set flag BEFORE stopping to prevent ondataavailable from processing late chunks
      isRecordingStoppedRef.current = true;

      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Keep stream active for next question (best practice: persist stream across recordings)
      // Don't stop tracks - this allows continuous camera preview between questions

      showStatus('Recording stopped. Ready to submit.', 'success');
    }
  };

  const submitAnswer = async () => {
    if (!uploadSessionRef.current) {
      showStatus('No upload session found', 'error');
      return;
    }

    try {
      console.log('🚀 Finalizing upload...');
      showStatus('Finalizing upload...', 'info');

      // Wait for queue to be fully processed
      console.log('⏳ Waiting for queue to complete...');
      let waitCount = 0;
      while ((uploadQueueRef.current.length > 0 || isProcessingQueueRef.current) && waitCount < 2000) {  // 200 seconds max
        await new Promise(resolve => setTimeout(resolve, 100));
        waitCount++;
        if (waitCount % 10 === 0) {  // Log every second
          console.log(`⏳ Queue: ${uploadQueueRef.current.length}, Processing: ${isProcessingQueueRef.current}`);
        }
      }

      if (uploadQueueRef.current.length > 0 || isProcessingQueueRef.current) {
        console.error('❌ Upload timeout - queue still processing after 200 seconds');
        showStatus('Upload timeout. Please try again.', 'error');
        return;
      }

      // Upload any remaining buffered chunks (split into exact parts for R2 compatibility)
      if (chunkBufferRef.current.length > 0) {
        console.log(`⬆️  Uploading remaining buffer: ${formatBytes(bufferSizeRef.current)}`);
        await uploadBufferedChunks(true);  // uploadAll = true for submit phase
      }

      // Wait for final queue processing to complete
      waitCount = 0;
      while ((uploadQueueRef.current.length > 0 || isProcessingQueueRef.current) && waitCount < 2000) {  // 200 seconds max
        await new Promise(resolve => setTimeout(resolve, 100));
        waitCount++;
        if (waitCount % 10 === 0) {  // Log every second
          console.log(`⏳ Final queue: ${uploadQueueRef.current.length}, Processing: ${isProcessingQueueRef.current}`);
        }
      }

      if (uploadQueueRef.current.length > 0 || isProcessingQueueRef.current) {
        console.error('❌ Final upload timeout after 200 seconds');
        showStatus('Upload timeout. Please try again.', 'error');
        return;
      }

      const totalUploaded = getTotalUploadedSize();
      console.log(`📊 Upload summary: ${uploadedPartsRef.current.length} parts, ${formatBytes(totalUploaded)} total`);

      if (uploadedPartsRef.current.length === 0) {
        showStatus('No video chunks uploaded', 'error');
        return;
      }

      // Complete the multipart upload
      const completeResponse = await fetch(`${apiBaseUrl}/upload/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: uploadSessionRef.current.session_id,
          parts: uploadedPartsRef.current
        })
      });

      if (!completeResponse.ok) {
        throw new Error('Failed to complete upload');
      }

      setUploadProgress(100);
      console.log('✨ Upload complete! Processing...');
      showStatus('Processing your response...', 'success');

      setTimeout(() => {
        nextQuestion();
      }, 2000);

    } catch (error) {
      showStatus('Upload failed: ' + error.message, 'error');
      setUploadProgress(0);
    }
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setRecordedChunks([]);
      setRecordingTime(0);
      setUploadProgress(0);
      setStatusMessage('');

      // Reset streaming upload state
      uploadSessionRef.current = null;
      uploadedPartsRef.current = [];
      currentPartNumberRef.current = 0;
      isUploadingRef.current = false;
      chunkBufferRef.current = [];
      bufferSizeRef.current = 0;
      uploadQueueRef.current = [];
      isProcessingQueueRef.current = false;
      isRecordingStoppedRef.current = false;  // Reset flag

      // Restore camera stream for next question (don't clear srcObject)
      if (videoRef.current && streamRef.current) {
        videoRef.current.src = '';
        videoRef.current.srcObject = streamRef.current;  // Restore stream, not null
      }
    } else {
      completeInterview();
    }
  };

  const completeInterview = async () => {
    try {
      // Stop camera stream when interview is completely finished
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }

      await fetch(`${apiBaseUrl}/interview/${token}/complete`, {
        method: 'POST'
      });
      showStatus('Interview completed successfully!', 'success');
    } catch (error) {
      showStatus('Error completing interview', 'error');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="interview-container">
      <div className="interview-header">
        <div className="header-info">
          <h1>{interviewData.job_title}</h1>
          <p className="company">{interviewData.company}</p>
          <p className="candidate">Candidate: {interviewData.candidate_name}</p>
        </div>
        <div className="question-counter">
          <span className="current">{currentQuestionIndex + 1}</span>
          <span className="separator">/</span>
          <span className="total">{totalQuestions}</span>
        </div>
      </div>

      <div className="interview-content">
        <div className="question-section">
          <h2>Question {currentQuestionIndex + 1}</h2>
          <p className="question-text">{currentQuestion.question_text}</p>
        </div>

        <div className="video-section">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={isRecording}
            className="video-player"
          />

          {isRecording && (
            <div className="recording-indicator">
              <span className="recording-dot" />
              <span className="recording-time">{formatTime(recordingTime)}</span>
            </div>
          )}
        </div>

        <div className="controls-section">
          {!streamRef.current && !isRecording && (
            <button onClick={setupCamera} className="btn btn-secondary">
              Setup Camera
            </button>
          )}

          {!isRecording && !hasRecording && streamRef.current && (
            <button onClick={startRecording} className="btn btn-primary">
              Start Recording
            </button>
          )}

          {isRecording && (
            <button onClick={stopRecording} className="btn btn-danger">
              Stop Recording
            </button>
          )}

          {hasRecording && !isRecording && (
            <>
              <button onClick={submitAnswer} className="btn btn-success">
                Submit Answer
              </button>
              <button onClick={async () => {
                // Abort current upload session if exists
                if (uploadSessionRef.current) {
                  try {
                    await fetch(`${apiBaseUrl}/upload/abort/${uploadSessionRef.current.session_id}`, {
                      method: 'POST'
                    });
                  } catch (error) {
                    console.error('Failed to abort upload:', error);
                  }
                }

                // Reset all state
                setRecordedChunks([]);
                uploadSessionRef.current = null;
                uploadedPartsRef.current = [];
                currentPartNumberRef.current = 0;
                isUploadingRef.current = false;
                setUploadProgress(0);

                if (videoRef.current) {
                  videoRef.current.src = '';
                }
              }} className="btn btn-secondary">
                Re-record
              </button>
            </>
          )}
        </div>

        {uploadProgress > 0 && (
          <div className="progress-section">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <span className="progress-text">{uploadProgress}%</span>
          </div>
        )}

        {statusMessage && (
          <div className={`status-message status-${statusType}`}>
            {statusMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewView;
